import React, { Component } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import propTypes from 'prop-types';

import ComponentsToolbox from 'app/ComponentsToolbox/components/ComponentsToolbox';
import RenderingEnvironment from 'app/RenderingEnvironment/components/RenderingEnvironment';
import { noop } from 'lib/noop';
import { PrimaryButton } from 'ui/Components';

import styles from './CreateNewMasterComponent.css';

const createComponentStage = { RenderingEnv: 1, Toolbox: 2 };

/**
 * This is a redux connected component. This component renders a button.
 * Clicking the button startes a wizard to choose the rendering environment
 * and type of new component.
 * Eventually the new component is added to existing page or container component.
 * @param {object} props - the props passed to this component
 * @returns {React.Component} the Explorer component
 */
export default class CreateNewMasterComponent extends Component {
  static propTypes = {
    checkStatusAndToggleCreateMasterComponent: propTypes.func.isRequired,
    className: propTypes.string,
    componentsList: propTypes.array,
    createNewComponent: propTypes.func,
    isReadonly: propTypes.bool,
    renderingEnvName: propTypes.string,
    setRenderingEnv: propTypes.func.isRequired,
    showCreateMasterComponentDialog: propTypes.bool,
    toggleCreateMasterComponent: propTypes.func.isRequired
  };

  static defaultProps = {
    className: '',
    componentsList: [],
    createNewComponent: noop,
    isReadonly: true,
    renderingEnvName: '',
    showCreateMasterComponentDialog: false
  };

  constructor(props) {
    super(props);
    this.state = {
      createComponentStage: createComponentStage.RenderingEnv
    };
    this.modalToggle = this.modalToggle.bind(this);
    this.setRenderingEnvironment = this.setRenderingEnvironment.bind(this);
    this.setCreateComponentStage = this.setCreateComponentStage.bind(this);
    this.onComponentSelected = this.onComponentSelected.bind(this);
  }

  /**
   * toggles component template modal and set selected component
   * @param {string} componentVariation - component variation of the selected component
   */
  onComponentSelected(componentVariation) {
    const { createNewComponent } = this.props;
    const { renderingEnv } = this.state;
    this.modalToggle();

    createNewComponent({ componentVariation, renderingEnv });
  }

  /**
   * sets selected rendering environment in this component state
   * @param {object} renderingEnv - selected rendering environment
   */
  setRenderingEnvironment(renderingEnv) {
    this.props.setRenderingEnv(renderingEnv);
    this.setState({ renderingEnv });
  }

  /**
   * closes rendering environment modal and opens component template modal
   * @param {string} stage - create component wizard stage
   */
  setCreateComponentStage(stage) {
    this.setState({ createComponentStage: stage });
  }

  /**
   * toggles the rendering environment modal state between open and close
   */
  modalToggle() {
    this.setState({ createComponentStage: createComponentStage.renderingEnv });
    this.props.toggleCreateMasterComponent();
  }

  /**
   * render modal stage
   * @returns {Component} stage component for create component wozard
   */
  renderStage() {
    switch (this.state.createComponentStage) {
      case createComponentStage.Toolbox:
        return (
          <ComponentsToolbox
            componentsList={this.props.componentsList}
            renderingEnvName={this.props.renderingEnvName}
            onCancel={() => this.setCreateComponentStage(createComponentStage.RenderingEnv)}
            onSelect={this.onComponentSelected}
            cancelText="Go Back"
          />
        );
      default:
        return (
          <RenderingEnvironment
            onCancel={this.modalToggle}
            onEnter={renderingEnv => {
              this.setRenderingEnvironment(renderingEnv);
              this.setCreateComponentStage(createComponentStage.Toolbox);
            }}
          />
        );
    }
  }

  render() {
    const {
      className,
      isReadonly,
      showCreateMasterComponentDialog,
      checkStatusAndToggleCreateMasterComponent
    } = this.props;
    return (
      <div className={styles.wrapper}>
        <PrimaryButton
          className={className}
          data-test-id="createNewMasterComponent-button"
          disabled={isReadonly}
          onClick={checkStatusAndToggleCreateMasterComponent}
        >
          Create New Master Component
        </PrimaryButton>
        <Modal isOpen={showCreateMasterComponentDialog} toggle={this.modalToggle}>
          <ModalBody className={styles.modal}>{this.renderStage()}</ModalBody>
        </Modal>
      </div>
    );
  }
}
